/**
 * The filter row above the invoice table (R5).
 *
 * THE SEARCH BOX MAKES A PROMISE AND THE SERVER DECIDES WHETHER IT CAN KEEP IT.
 * R3 searches Bexio rows over document number and title, and native rows over
 * the invoice number — neither searches the tenant. A box labelled only "suchen"
 * beside a column headed "Mieter / Objekt" implies otherwise, and an operator
 * who types a tenant's name into it gets an empty table and concludes the
 * invoice does not exist. So the placeholder names the columns that ARE covered
 * and the hint names the one that is not. That sentence is load-bearing.
 *
 * "ALLE" IS AN ABSENT PARAMETER, NOT A WORD. R3 answers a literal `status=all`
 * with a 422, and the UI's "Alle" means no status filter at all — so the option
 * carries the empty string and the data hook omits the parameter entirely.
 *
 * THE MONTH IS THE INVOICE DATE. Vantio also has a rent period, and for the same
 * invoice it is frequently a different month: an invoice raised on 2 October can
 * bill September. The control says which of the two it filters, because a reader
 * who assumes the other one gets a plausible, wrong answer rather than an empty
 * one.
 *
 * NO SOURCE CONTROL, ON PURPOSE. The source is a view switch above the cards and
 * lives in the URL; offering it here as one filter among several would invite the
 * value this product does not have, which is "both".
 *
 * THE EXPORT BUTTON SITS HERE RATHER THAN IN THE PAGE HEADER (R8), because the
 * export IS this row: same source, same view, same filters, same sort — just
 * every matching row instead of the visible page. A button further away would
 * invite the reading that it exports "the invoices", which is a different and
 * much larger claim.
 *
 * IT IS OPTIONAL, AND ITS ABSENCE MEANS SOMETHING. Every caller written before
 * R8 passes no handler and renders no button; a surface that cannot offer a file
 * shows nothing rather than a disabled control, because a disabled control says
 * "not right now" where the truth is "not at all". A control that is present but
 * disabled means the population is empty or a run is already going.
 *
 * The layout is `BexioFilterBar` unchanged. It carries no Bexio semantics — it
 * lays out a search box, some controls and a reset — and reusing it keeps this
 * row and the two Bexio workspace rows from drifting apart.
 */
import React from "react";
import { Download } from "lucide-react";

import BexioFilterBar, {
  BexioFilterSelect,
} from "../../../components/admin/bexio/workspace/BexioFilterBar";
import PeriodPicker from "../uebersicht/PeriodPicker";

/** The four words the product speaks about an invoice, plus "no filter". */
const STATUS_OPTIONS = [
  { value: "", label: "Alle" },
  { value: "paid", label: "Bezahlt" },
  { value: "unpaid", label: "Offen" },
  { value: "overdue", label: "Überfällig" },
  { value: "cancelled", label: "Storniert" },
];

/** What each source's list endpoint actually looks at, stated per source. */
const SEARCH_SCOPE = {
  bexio: {
    placeholder: "Beleg-Nr. oder Titel suchen …",
    scope: "Die Suche umfasst Beleg-Nr. und Titel, nicht den Mieternamen.",
  },
  native: {
    placeholder: "Rechnungsnummer suchen …",
    scope: "Die Suche umfasst die Rechnungsnummer, nicht den Mieternamen.",
  },
};

const PERIOD_SCOPE = "Der Monatsfilter bezieht sich auf das Rechnungsdatum.";

export default function InvoiceFilters({
  source,
  q,
  onQChange,
  status,
  onStatusChange,
  period,
  onPeriodChange,
  onReset,
  canReset = false,
  resultLabel = "",
  onExport,
  exporting = false,
  canExport = false,
}) {
  const search = SEARCH_SCOPE[source] || SEARCH_SCOPE.native;

  return (
    <BexioFilterBar
      searchValue={q}
      onSearchChange={onQChange}
      searchLabel="Rechnungen durchsuchen"
      searchPlaceholder={search.placeholder}
      searchHint={`${search.scope} ${PERIOD_SCOPE}`}
      onReset={onReset}
      canReset={canReset}
      resultLabel={resultLabel}
    >
      <BexioFilterSelect
        label="Status"
        value={status}
        onChange={onStatusChange}
        options={STATUS_OPTIONS}
      />

      {/* The picker's trigger names the selected range; the group names what the
          range MEANS, so the accessible name is not just "September 2026". */}
      <div
        role="group"
        aria-label="Rechnungsmonat"
        className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground"
      >
        <span className="sr-only sm:not-sr-only">Rechnungsmonat</span>
        <PeriodPicker value={period} onChange={onPeriodChange} />
      </div>

      {/* R9: `aria-busy` because `disabled` removes the button from the tab
          order and says nothing about WHY, and the label change to
          "Exportiere …" is silent to a reader not already focused on it. */}
      {onExport ? (
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || !canExport}
          aria-busy={exporting}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download aria-hidden="true" className="h-3.5 w-3.5" />
          {exporting ? "Exportiere …" : "Exportieren"}
        </button>
      ) : null}
    </BexioFilterBar>
  );
}
